import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import LinkItem from 'components_v2/common/LinkList/LinkItem';
import Card from 'components_v2/common/CardV2';

function renderTimeStamp(item, timeStampFun, timeStampFormat) {
  let timestampHtml = null;
  const timestamp = item.upd || item.lpt || item.dl || null;
  if (timestamp && timeStampFun && timeStampFormat) {
    timestampHtml = timeStampFun(timestamp, timeStampFormat);
  }
  return timestampHtml;
}

const shouldShowImageListing = item => {
  if (
    item &&
    !(
      typeof item.image === 'object' ||
      (item.imageid && item.imagesize) ||
      (item.imageid && item.imgsize) ||
      item.imageUrl
    )
  ) {
    return false;
  }

  return true;
};

function renderList(
  data,
  itemWrapperClass,
  textItemWrapperClass,
  tileType = 'isVerticalTile',
  cssObj,
  newga,
  timeStampFun,
  timeStampFormat,
  hideImage,
  itemClass,
  restProps,
  showDuration,
  showPhotosCount,
  currentIndex,
  cardClass,
  ga4Category,
  ga4Type,
) {
  const { htmlKeys } = restProps;

  if (!Array.isArray(data)) {
    return null;
  }

  const itemWrapperCss = classNames({
    [itemWrapperClass]: true,
    [textItemWrapperClass]: hideImage,
  });
  const spaceRemoverGA = (val, index) =>
    val.replace(/ /g, '').concat('-', currentIndex + index, '#');

  return data.map((item, index) => {
    const spotlightGA =
      item.ag && item.ag === 'spotlight' ? `sl_${index + 1}_${item.id}` : null;
    return (
      <div
        className={`${itemWrapperCss} ${
          tileType !== 'isVerticalTile' && cardClass === 'cardwithoutthumb'
            ? 'no_bdr_last'
            : ''
        }`}
        key={`non_featured_${index}`}
      >
        {hideImage || !shouldShowImageListing(item) ? (
          <LinkItem
            item={item}
            className={`${itemClass} ${
              item.tn === 'liveblog' ? 'lbBlinker2' : null
            }`}
            htmlKeys={htmlKeys}
            newGA={`${spaceRemoverGA(newga, index + 1)}${item.wu}`}
            linkType2
            ga4Type={ga4Type}
            ga4Category={ga4Category}
            gaIndex={index + 1}
          />
        ) : (
          [
            tileType !== 'isVerticalTile' ? (
              <Card
                data={item}
                newga={`${spaceRemoverGA(newga, index + 1)}${item.wu}`}
                key={item.id || `${item.tn}_${index}`}
                timeStampHtml={renderTimeStamp(
                  item,
                  timeStampFun,
                  timeStampFormat,
                )}
                cssObj
                restProps={restProps}
                showDuration={showDuration}
                showPhotosCount={showPhotosCount}
                viewga={spotlightGA}
                cardClass={cardClass}
                ga4Type={ga4Type}
                ga4Category={ga4Category}
                gaIndex={index + 1}
              />
            ) : (
              <Card
                data={item}
                isVerticalTile
                newga={`${spaceRemoverGA(newga, index + 1)}${item.wu}`}
                key={item.id || `${item.tn}_${index}`}
                timeStampHtml={renderTimeStamp(
                  item,
                  timeStampFun,
                  timeStampFormat,
                )}
                cssObj
                restProps={restProps}
                showDuration={showDuration}
                showPhotosCount={showPhotosCount}
                viewga={spotlightGA}
                cardClass={cardClass}
                ga4Type={ga4Type}
                ga4Category={ga4Category}
                gaIndex={index + 1}
              />
            ),
          ]
        )}
      </div>
    );
  });
}

function CardListV5(props) {
  const {
    data = [],
    featuredItemDisplay: {
      cssObj = { figureClass: '', imgCont: '', titleCont: '' },
      featuredIndex,
      ...restFeatProps
    } = {},
    nonFeaturedDisplay = {
      listWrapperClass: '',
      itemWrapperClass: '',
      textItemWrapperClass: '',
      tileType: null,
      hideImage: null,
      cssObj: { figureClass: 'linktype2', imgCont: '', titleCont: '' },
      itemClass: '',
    },
    timestampData = {
      timeStampFun: () => {},
      timeStampFormat: null,
    },
    newga,
    currentIndex,
    isEtimes,
    ga4Type,
    ga4Category = '',
    ...restProps
  } = props;
  const featured = isEtimes
    ? data
    : data instanceof Array &&
      data.filter(
        (item, i) =>
          item.tn === 'featured' || featuredIndex === i ? item : null,
      );

  const nonFeatured =
    data instanceof Array &&
    !isEtimes &&
    data.filter(
      (item, i) =>
        item.tn !== 'featured' && featuredIndex !== i ? item : null,
    );

  return (
    <>
      {data instanceof Array &&
        featured.map((item, index) => {
          const spotlightGA =
            item.ag && item.ag === 'spotlight'
              ? `sl_${index + 1}_${item.id}`
              : null;
          return (
            <Card
              data={item}
              cssObj={cssObj}
              newga={`${newga}0/${item.wu}`}
              key={item.id || `${item.tn}_${index}`}
              timeStampHtml={renderTimeStamp(
                item,
                timestampData.timeStampFun,
                timestampData.timeStampFormat,
              )}
              {...restFeatProps}
              restProps={restProps}
              viewga={spotlightGA}
              ga4Type={ga4Type}
              ga4Category={ga4Category}
              gaIndex={item.gaindex}
            />
          );
        })}
      {nonFeaturedDisplay.listWrapperClass ? (
        <div className={nonFeaturedDisplay.listWrapperClass}>
          {renderList(
            nonFeatured,
            nonFeaturedDisplay.itemWrapperClass,
            nonFeaturedDisplay.textItemWrapperClass,
            nonFeaturedDisplay.tileType,
            nonFeaturedDisplay.cssObj,
            newga,
            timestampData.timeStampFun,
            timestampData.timeStampFormat,
            nonFeaturedDisplay.hideImage,
            nonFeaturedDisplay.itemClass,
            restProps,
            typeof nonFeaturedDisplay.showDuration === 'boolean'
              ? nonFeaturedDisplay.showDuration
              : true,
            typeof nonFeaturedDisplay.showPhotosCount === 'boolean'
              ? nonFeaturedDisplay.showPhotosCount
              : true,
            currentIndex,
            nonFeaturedDisplay.cardClass,
            ga4Category,
            ga4Type,
          )}
        </div>
      ) : (
        renderList(
          nonFeatured,
          nonFeaturedDisplay.itemWrapperClass,
          nonFeaturedDisplay.textItemWrapperClass,
          nonFeaturedDisplay.tileType,
          nonFeaturedDisplay.cssObj,
          newga,
          timestampData.timeStampFun,
          timestampData.timeStampFormat,
          nonFeaturedDisplay.hideImage,
          nonFeaturedDisplay.itemClass,
          restProps,
          typeof nonFeaturedDisplay.showDuration === 'boolean'
            ? nonFeaturedDisplay.showDuration
            : true,
          typeof nonFeaturedDisplay.showPhotosCount === 'boolean'
            ? nonFeaturedDisplay.showPhotosCount
            : true,
          currentIndex,
          nonFeaturedDisplay.cardClass,
          ga4Category,
          ga4Type,
        )
      )}
    </>
  );
}

CardListV5.propTypes = {
  data: PropTypes.shape([]).isRequired,
  featuredItemDisplay: PropTypes.shape({}),
  nonFeaturedDisplay: PropTypes.shape({}),
  timestampData: PropTypes.shape({}),
  newga: PropTypes.string,
  currentIndex: PropTypes.number,
  showSecName: PropTypes.bool,
};

CardListV5.defaultProps = {
  featuredItemDisplay: {},
  nonFeaturedDisplay: {},
  timestampData: {},
  newga: '',
  currentIndex: '',
  showSecName: false,
};

export default CardListV5;
